/* counter.damga.qa: the till as a kiosk.
 *
 * THE SAME PRODUCT AS THE DASHBOARD (ruled 2026-09-18, and the entry point kept on
 * purpose): the same merchant sign-in, the same till component, the same API. A
 * tablet on a stand opens straight to the keypad, installs as an app and keeps its
 * queue across a restart, and every printed counter card that names this address
 * still works. What it no longer has is a sign-in of its own: Google, Microsoft
 * and the emailed code were the customer's doors and came off the merchant side.
 */
import { MerchantSignIn, Wordmark } from "@damga/ds";
import {
  Forbidden,
  Offline,
  SessionExpired,
  type Session,
  createClient,
  createMerchantAuth,
  isRefusal,
  merchantRefreshUrl,
  readSession,
  t,
  writeSession,
} from "@damga/kit";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { API_URL, AUTH_URL } from "./config";
import { Till, type TillBusiness } from "./Till";
import { signInWords } from "./words";

export { type EarnResponse } from "./Till";

export function App() {
  const [session, setSessionState] = useState<Session | null>(() => readSession<Session>());
  const [business, setBusiness] = useState<TillBusiness | null>(null);

  const setSession = useCallback((s: Session | null) => {
    writeSession(s);
    setSessionState(s);
    if (!s) setBusiness(null);
  }, []);
  const sessionRef = useRef(session);
  sessionRef.current = session;

  const api = useMemo(
    () =>
      createClient({
        apiUrl: API_URL,
        authUrl: AUTH_URL,
        refreshUrl: merchantRefreshUrl(API_URL),
        getSession: () => sessionRef.current,
        setSession,
      }),
    [setSession],
  );
  const merchantAuth = useMemo(() => createMerchantAuth(API_URL), []);

  useEffect(() => {
    if (!session) return;
    void (async () => {
      try {
        const res = await api.call<TillBusiness>("/me/business");
        if (!isRefusal(res)) setBusiness(res as unknown as TillBusiness);
      } catch (err) {
        // A session from before 2026-09-18 came in by the customer's door, and the
        // till no longer opens for one: sign in again, by the merchant's.
        if (err instanceof SessionExpired) setSession(null);
        else if (err instanceof Forbidden && err.code === "E_MERCHANT_SIGN_IN") setSession(null);
        else if (err instanceof Offline) {
          /* the till still shows; the queue takes the stamps */
        }
      }
    })();
  }, [api, session, setSession]);

  const endShift = useCallback(() => {
    const current = sessionRef.current;
    if (current) void merchantAuth.signOut(current.refresh_token);
    setSession(null);
  }, [merchantAuth, setSession]);

  if (!session) {
    return (
      <MerchantSignIn
        auth={merchantAuth}
        words={signInWords}
        brand={<Wordmark size={18} />}
        onSignedIn={setSession}
      />
    );
  }

  return (
    <Till
      api={api}
      business={business}
      onSessionEnded={() => setSession(null)}
      endLabel={t("counter.endShift")}
      onEnd={endShift}
    />
  );
}
